import React, { FC, useEffect, useState, useMemo, useCallback } from "react";
import { Product } from "data/data";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NcImage from "shared/NcImage/NcImage";
import Cookies from "js-cookie";
import { useOnlineOrderSettings } from "context/OnlineOrderSettingsContext";
import { useCartDataProvider } from "context/CartProductContext";
import { validateModifiers, getModifierPrice } from "utils/modifierHelpers";
import ModifierSection from "components/ProductCustomization/ModifierSection";
import { MinusIcon, PlusIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { ShoppingBagIcon } from "@heroicons/react/24/solid";
import { ProductCardSkeleton } from "shared/Skeleton/Skeleton";
import toast from "react-hot-toast";
import { Transition } from "@headlessui/react";

export interface ProductDetailPageProps {
  className?: string;
}

const ProductDetailPage: FC<ProductDetailPageProps> = ({ className = "" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { dispatch } = useCartDataProvider();
  const { items, isLoading } = useOnlineOrderSettings();
  const paramId: any = useParams();
  const id = paramId.id ? parseInt(paramId.id) : undefined;
  const merchantname = Cookies.get("merchantname");

  const [modifierState, setModifierState] = useState<
    Record<number, Record<number, boolean>>
  >(location.state?.modifierChecked || {});
  const [quantity, setQuantity] = useState(location.state?.quantity || 1);
  const [note, setNote] = useState(location.state?.note || "");
  const [showError, setShowError] = useState(false);

  const isEditMode = !!location.state?.cartItemId;

  // Find the product from context data
  const product = useMemo(() => {
    if (!items || !id) return null;
    for (const category of items as any[]) {
      const found = category.items?.find((item: Product) => item.id === id);
      if (found) return found as Product;
    }
    return null;
  }, [items, id]);

  // Initialize modifier state from edit mode
  useEffect(() => {
    if (location.state?.modifierChecked) {
      setModifierState(location.state.modifierChecked);
    }
  }, [location.state]);

  const handleModifierChange = useCallback(
    (modifierSetId: number, itemId: number, checked: boolean) => {
      setModifierState((prev) => {
        const updated = { ...prev };
        if (!updated[modifierSetId]) updated[modifierSetId] = {};
        if (checked) {
          updated[modifierSetId][itemId] = true;
        } else {
          delete updated[modifierSetId][itemId];
        }
        return updated;
      });
      setShowError(false);
    },
    []
  );

  const runningTotal = useMemo(() => {
    if (!product) return 0;
    const modPrice = getModifierPrice(product, modifierState);
    return (product.onlinePrice + modPrice) * quantity;
  }, [product, modifierState, quantity]);

  const handleAddToCart = () => {
    if (!product) return;

    const validation = validateModifiers(product, modifierState);
    if (!validation.valid) {
      setShowError(true);
      return;
    }

    const cartProduct = {
      ...product,
      quantity,
      modifierChecked: modifierState,
      note,
    };

    if (isEditMode) {
      dispatch({
        type: "EDIT_CART",
        payload: { ...cartProduct, cartItemId: location.state.cartItemId },
      });
    } else {
      dispatch({ type: "ADD_TO_CART", payload: cartProduct });
    }

    toast.custom(
      (t) => (
        <Transition
          appear
          show={t.visible}
          className="p-3 max-w-sm w-full bg-white dark:bg-slate-800 shadow-lg rounded-xl pointer-events-auto ring-1 ring-black/5 dark:ring-white/10 text-slate-900 dark:text-slate-100"
          enter="transition-all duration-200"
          enterFrom="opacity-0 translate-y-4"
          enterTo="opacity-100 translate-y-0"
          leave="transition-all duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100 dark:bg-slate-700">
              {product.imageUrl && (
                <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{product.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {isEditMode ? "Updated in cart" : "Added to cart"}
              </p>
            </div>
          </div>
        </Transition>
      ),
      { position: "bottom-center", id: `detail-${product.id}`, duration: 1500 }
    );

    navigate(`/${merchantname}`);
  };

  // Loading state
  if (isLoading || !items) {
    return (
      <div className="container py-10">
        <div className="max-w-2xl mx-auto">
          <ProductCardSkeleton className="w-full" />
        </div>
      </div>
    );
  }

  // Product not found
  if (!product) {
    return (
      <div className="container py-16 text-center">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">
          Item not found
        </h2>
        <p className="text-slate-500 dark:text-slate-400 mb-6">
          This menu item may no longer be available.
        </p>
        <button
          onClick={() => navigate(`/${merchantname}`)}
          className="px-6 py-2.5 bg-primary-500 dark:bg-primary-400 text-white dark:text-slate-900 rounded-full font-semibold text-sm"
        >
          Back to Menu
        </button>
      </div>
    );
  }

  return (
    <div className={`nc-ProductDetailPage bg-white dark:bg-slate-900 min-h-[60vh] ${className}`}>
      {/* Back button */}
      <div className="container pt-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back
        </button>
      </div>

      <main className="container pb-24 pt-4">
        <div className="max-w-3xl mx-auto lg:flex lg:max-w-5xl lg:gap-10">
          {/* Image */}
          <div className="lg:w-[45%]">
            <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-stone-100 dark:bg-slate-800">
              <NcImage
                containerClassName="w-full h-full"
                src={product.imageUrl || ""}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div className="lg:w-[55%] mt-6 lg:mt-0">
            {/* Name & price */}
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">
              {product.name}
            </h1>
            <p className="text-xl font-bold text-primary-600 dark:text-primary-400 mt-2">
              ${product.onlinePrice.toFixed(2)}
              {product.unit?.name && (
                <span className="text-sm font-normal text-slate-400 ml-1">
                  / {product.unit.name}
                </span>
              )}
            </p>
            {product.description && (
              <p className="text-slate-500 dark:text-slate-400 mt-3">
                {product.description}
              </p>
            )}

            {/* Modifier sections */}
            {product.modifierSets && product.modifierSets.length > 0 && (
              <div className="mt-6 divide-y divide-slate-100 dark:divide-slate-700/50 border-t border-slate-100 dark:border-slate-700/50">
                {product.modifierSets.map((modSet) => (
                  <ModifierSection
                    key={modSet.id}
                    modifierSet={modSet}
                    selectedState={modifierState[modSet.id] || {}}
                    onChange={handleModifierChange}
                    showError={showError}
                  />
                ))}
              </div>
            )}

            {/* Note */}
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Special Instructions
              </h4>
              <textarea
                placeholder="Any special requests?"
                className="w-full h-16 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent resize-none"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Sticky footer */}
      <div className="fixed bottom-0 inset-x-0 z-40 px-4 py-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.3)]">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          {/* Quantity */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-full px-1 py-1">
            <button
              onClick={() => setQuantity((q: number) => Math.max(1, q - 1))}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors"
              disabled={quantity <= 1}
            >
              <MinusIcon className="w-5 h-5" />
            </button>
            <span className="w-8 text-center font-bold text-slate-900 dark:text-slate-100">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity((q: number) => q + 1)}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Add to cart */}
          <button
            onClick={handleAddToCart}
            disabled={!product.isAvailable}
            className={`flex-1 h-12 flex items-center justify-center gap-2 rounded-xl font-bold text-base shadow-md active:scale-[0.98] transition-all ${
              product.isAvailable
                ? "bg-accent-500 dark:bg-accent-400 text-white dark:text-slate-900 hover:bg-accent-600 dark:hover:bg-accent-500"
                : "bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed"
            }`}
          >
            <ShoppingBagIcon className="w-5 h-5" />
            <span>
              {isEditMode ? "Update" : "Add to Cart"} - ${runningTotal.toFixed(2)}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
